<script setup lang="ts">
import { useWorkOrderDetails } from '@/composables/rc/work-order-details'
import { getAllMinerStatuses, getContainers } from '@/services/rc.service'
import { bulkUpdateMiners } from '@/services/rc/miners.service'
import { MinerBulkActions } from '@/types/rc/miners/BulkMinerActions'
import type { IContainerDashboard } from '@/types/rc/containers'
import {
  RWOOwnership,
  type IContainerDashboardFilters,
  type IWorkOrderDashboardMinersFilters,
  type IWorkOrderFilters,
} from '@/types/rc/filters'
import type { IBulkUpdateMinersForm } from '@/types/rc/forms'
import type { MinerStatus } from '@/types/rc/miners/MinerStatus'
import { ShipmentType } from '@/types/rc/shipments'
import type { IListWorkOrder, IService } from '@/types/rc/work-orders'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { type CheckboxValueType } from 'element-plus'
import { computed, onMounted, ref } from 'vue'
import { Icon } from '@iconify/vue'
import { HiddenRepairServices } from '@/types/rc/services'
import { assignMinersToContainer } from '@/services/rc/containers.service'
import { getWorkOrders, moveMinersToWorkOrder } from '@/services/rc/work-orders.service'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'

const queryClient = useQueryClient()

const props = defineProps<{
  miners: {
    id: number
    model: string
    sn: string
  }[]
  selectedMinersAmount: number
  action: MinerBulkActions
  workOrderNumber: string
  workOrderCustomerId: number
  filters?: IWorkOrderDashboardMinersFilters
}>()

const minerBulkActionToLegend = computed((): Record<MinerBulkActions, string> => ({
  [MinerBulkActions.StatusUpdate]: `You are updating the status of ${props.selectedMinersAmount} miners`,
  [MinerBulkActions.OutboundContainerAssignment]: `You are assigning ${props.selectedMinersAmount} miners to an outbound container`,
  [MinerBulkActions.CompleteServices]: `You are marking the services of ${props.selectedMinersAmount} miners as completed`,
  [MinerBulkActions.WorkOrderMovement]: `You are moving ${props.selectedMinersAmount} miners to another work order`,
  [MinerBulkActions.Consolidate]: '', // Needed to avoid type issues, but won't needed
}))

const open = defineModel<boolean>()

// Status Update refs, mutations and functions
const selectedNewStatus = ref<MinerStatus>()
const minerStatusFilterOptions = ref<string[]>([])
const { isPending: bulkUpdateIsPending, mutateAsync: bulkUpdateMutateAsync } = useMutation({
  mutationFn: (form: IBulkUpdateMinersForm) => bulkUpdateMiners(form),
  onError: (e) => {
    NotifyFail(e.message)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-miners', props.workOrderNumber] })
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-summary', props.workOrderNumber] })
    NotifySuccess(`Miners updated successfully`)
  },
})

// Outbound container assignment refs, mutations and functions
const selectedContainer = ref<IContainerDashboard>()
const containerFilterOptions = ref<{ label: string; value: IContainerDashboard }[]>([])
const containerFilterLoading = ref(false)
const { isPending: assignMinersToContainerIsPending, mutateAsync: assignMinersToContainerMutateAsync } = useMutation({
  mutationFn: ({ id, minerIDs }: { id: number; minerIDs: number[] }) => assignMinersToContainer(id, minerIDs),
  onError: (e) => {
    NotifyFail(e.message)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-miners', props.workOrderNumber] })
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-summary', props.workOrderNumber] })
    NotifySuccess(`Miners updated successfully`)
  },
})

// Work Order Movement assignment refs, mutations and functions
const selectedWorkOrder = ref<IListWorkOrder>()
const workOrderFilterOptions = ref<{ label: string; value: IListWorkOrder }[]>([])
const workOrderFilterLoading = ref(false)
const { mutateAsync: workOrderMinerMovementMutateAsync } = useMutation({
  mutationFn: ({ workOrderNumber, minerIDs }: { workOrderNumber: string; minerIDs: number[] }) =>
    moveMinersToWorkOrder(workOrderNumber, minerIDs),
  onError: (e) => {
    NotifyFail(e.message)
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-miners', props.workOrderNumber] })
    queryClient.invalidateQueries({ queryKey: ['work-order-dashboard-summary', props.workOrderNumber] })
    NotifySuccess(`Miners updated successfully`)
  },
})

// Complete Services refs, mutations and functions
const { data: workOrderDetails } = useWorkOrderDetails(props.workOrderNumber)
const workOrderServices = computed<IService[]>(
  () =>
    workOrderDetails.value?.scope_of_work.filter((s: IService) => !HiddenRepairServices.includes(s.description)) ?? []
)
const checkAll = ref<boolean>(false)
const isIndeterminate = ref<boolean>(true)
const selectedServices = ref<number[]>([])

const onCheckAllChange = (val: CheckboxValueType) => {
  selectedServices.value = val ? workOrderServices.value.map((s: IService) => s.id) : []
  isIndeterminate.value = false
  onCheckChange()
}

const onCheckChange = () => {
  // Change checkAll value depending on selected elements
  const checkedCount = selectedServices.value.length
  checkAll.value = checkedCount === workOrderServices.value.length
  isIndeterminate.value = checkedCount > 0 && checkedCount < workOrderServices.value.length
}

/**
 * Remote method for container filter.
 *
 * @param query container query fragment.
 */
const filterContainers = async (query: string) => {
  try {
    containerFilterLoading.value = true

    const filters: IContainerDashboardFilters = {
      page: 1,
      per_page: 10,
      work_order_number: props.workOrderNumber,
      kind: ShipmentType.Outbound,
      fragment: query,
    }

    const filteredContainers = await getContainers(filters)
    containerFilterOptions.value = filteredContainers.data.map((c: IContainerDashboard) => {
      return {
        label: c.number,
        value: c,
      }
    })
  } catch {
    NotifyFail('Failed to filter containers')
    containerFilterOptions.value = []
  } finally {
    containerFilterLoading.value = false
  }
}

/**
 * Remote method for work order filter.
 *
 * @param query container query fragment.
 */
const filterWorkOrders = async (query: string) => {
  try {
    workOrderFilterLoading.value = true

    const filters: IWorkOrderFilters = {
      page: 1,
      per_page: 10,
      customer_id: props.workOrderCustomerId,
      owner: RWOOwnership.OUR_WORK,
      number: query,
    }

    const filteredWorkOrders = await getWorkOrders(filters)
    workOrderFilterOptions.value = filteredWorkOrders.data
      .filter((wo: IListWorkOrder) => wo.number != props.workOrderNumber)
      .map((wo: IListWorkOrder) => {
        return {
          label: wo.number,
          value: wo,
        }
      })
  } catch {
    NotifyFail('Failed to filter work orders')
    workOrderFilterOptions.value = []
  } finally {
    workOrderFilterLoading.value = false
  }
}

// Loading state
const isLoading = computed<boolean>(() => {
  return bulkUpdateIsPending.value || assignMinersToContainerIsPending.value
})
/**
 * Handles form submit.
 * Executes the appropriate mutation depending on selected action
 */
const executeAction = async (action: MinerBulkActions) => {
  const minerIDs = props.miners.length > 0 ? props.miners.map((miner) => miner.id) : undefined

  try {
    switch (action) {
      case MinerBulkActions.StatusUpdate:
        await bulkUpdateMutateAsync({
          work_order_number: props.workOrderNumber,
          miner_ids: minerIDs,
          miner_filters: props.filters,
          status: selectedNewStatus.value,
        })
        open.value = false
        break
      case MinerBulkActions.CompleteServices:
        await bulkUpdateMutateAsync({
          work_order_number: props.workOrderNumber,
          miner_ids: minerIDs,
          miner_filters: props.filters,
          completed_services: selectedServices.value,
        })
        open.value = false
        break
      case MinerBulkActions.OutboundContainerAssignment:
        await assignMinersToContainerMutateAsync({
          id: selectedContainer.value!.id,
          minerIDs: minerIDs!,
        })
        open.value = false
        break
      case MinerBulkActions.WorkOrderMovement:
        await workOrderMinerMovementMutateAsync({
          workOrderNumber: selectedWorkOrder.value!.number,
          minerIDs: minerIDs!,
        })
        open.value = false
        break
      default:
        NotifyFail('Invalid action: ' + action)
        break
    }
  } catch {
    // Error already handled by mutations
  }
}

/**
 * Reset form, reset mutations, emit closed (unsets customer ID prop).
 */
const handleClosed = () => {
  selectedNewStatus.value = undefined
  selectedContainer.value = undefined
  selectedWorkOrder.value = undefined
  selectedServices.value = []
  containerFilterOptions.value = []
  workOrderFilterOptions.value = []
}

/**
 * Enables submit button
 */
const isActionReady = computed<boolean>(
  () =>
    (selectedServices.value !== undefined && selectedServices.value.length >= 1) ||
    selectedNewStatus.value !== undefined ||
    selectedContainer.value !== undefined ||
    selectedWorkOrder.value !== undefined
)

/**
 * On mount: fetch resources depending on action
 */
onMounted(async () => {
  try {
    minerStatusFilterOptions.value = await getAllMinerStatuses()
  } catch {
    NotifyFail('Failed to fetch resources')
  }
})
</script>

<template>
  <el-dialog v-model="open" :width="600" :title="props.action" :show-close="false" @closed="handleClosed">
    <p>{{ minerBulkActionToLegend[props.action] }}</p>
    <div v-if="props.miners.length > 0" class="max-h-[132px] overflow-y-auto">
      <br />
      <ul class="list-disc list-inside">
        <li v-for="miner in props.miners" :key="miner.id">{{ miner.model }} - {{ miner.sn }}</li>
      </ul>
    </div>
    <br />
    <div v-if="props.action == MinerBulkActions.StatusUpdate" class="flex flex-col gap-2">
      <p>Choose the new status</p>
      <el-select v-model="selectedNewStatus" placeholder="Choose a status" clearable>
        <el-option v-for="status in minerStatusFilterOptions" :key="status" :label="status" :value="status" />
      </el-select>
    </div>
    <div v-if="props.action == MinerBulkActions.OutboundContainerAssignment" class="flex flex-col gap-2">
      <p>Choose a container</p>
      <el-select-v2
        v-model="selectedContainer"
        :options="containerFilterOptions"
        :loading="containerFilterLoading"
        remote
        :remote-method="filterContainers"
        filterable
        clearable
        value-key="id"
        placeholder="Search a container"
        loading-text="Loading containers..."
      />
    </div>
    <div v-if="props.action == MinerBulkActions.WorkOrderMovement" class="flex flex-col gap-2">
      <p>Choose a destination work order</p>
      <el-select-v2
        v-model="selectedWorkOrder"
        :options="workOrderFilterOptions"
        :loading="workOrderFilterLoading"
        remote
        :remote-method="filterWorkOrders"
        filterable
        clearable
        value-key="number"
        placeholder="Search a work order"
        loading-text="Loading work order..."
      />
    </div>
    <div v-if="props.action == MinerBulkActions.CompleteServices" class="flex flex-col gap-2">
      <p>Choose the services to complete</p>
      <el-checkbox v-model="checkAll" :indeterminate="isIndeterminate" :disabled="isLoading" @change="onCheckAllChange"
        >All</el-checkbox
      >
      <el-checkbox-group v-model="selectedServices" :disabled="isLoading" @change="onCheckChange">
        <el-checkbox v-for="svc in workOrderServices" :key="svc.id" :label="svc.description" :value="svc.id">
          <div class="flex flex-row items-center gap-1">
            <Icon :icon="svc.icon" :width="20" />
            <span>{{ svc.description }}</span>
          </div>
        </el-checkbox>
      </el-checkbox-group>
    </div>
    <template #footer>
      <el-button :disabled="isLoading" @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="isLoading" :disabled="!isActionReady" @click="executeAction(props.action)"
        >Accept</el-button
      >
    </template>
  </el-dialog>
</template>
